/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kCoreHandlerReport - kCore's error-report relay members (KServicesURL, KFitReport, KReportError and the report throttle).
 * Split out of kCoreHandler.ts (2026-10-19, owner: smaller files for the reuse reviewer). The bodies are
 * moved VERBATIM except that `this` is the explicit first parameter `Self` (private/protected members are
 * read as Self["Name"]); kCore keeps every member as a same-signature delegating member.
 */

import type { kCore } from "./kCoreHandler";

export class kCoreHandlerReport {
    /**
     * A report in exactly the shape the relay accepts: the ten fields and nothing else, every text a
     * string cut to its limit, ErrorNumber an integer 0..10^7, and a ClientEmail that is not
     * e-mail shaped sent as "" (the relay refuses anything else there).
     *
     * DOCUMENTED EXCEPTION: no error-mode guard and no handler call, deliberately. It runs ON the
     * reporting path, i.e. only while KErrorMode is already true, so a guard would blank every
     * report; and KReportError's own catch is the one place a failure here can go.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static KFitReport = (Self: typeof kCore, Data: any): { [Field: string]: string | number } => {
        const Fit: { [Field: string]: string | number } = {};
        let Source: { [Field: string]: unknown } = {};
        if (Data !== null && Data !== undefined && typeof Data === "object") { Source = Data; }
        for (const Field of Object.keys(Self["kReportLimits"])) {
            const Value: unknown = Source[Field];
            let Text: string = "";
            if (Value !== null && Value !== undefined) { Text = String(Value); }
            Fit[Field] = Text.substring(0, Self["kReportLimits"][Field]);
        }
        let Line: number = Math.floor(Number(Source.ErrorNumber));
        if (isNaN(Line) === true || Line < 0) { Line = 0; }
        if (Line > 10000000) { Line = 10000000; }
        Fit.ErrorNumber = Line;
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(Fit.ClientEmail)) === false) { Fit.ClientEmail = ""; }
        if (String(Fit.ErrorDescription).trim() === "") { Fit.ErrorDescription = "(no description)"; }
        if (String(Fit.ProductName).trim() === "") { Fit.ProductName = "(unknown product)"; }
        // The relay also refuses a report over 16 KB (HTTP 413), and that is counted in BYTES: text
        // outside ASCII is up to three bytes a character, so the character limits above do not
        // guarantee it. The two long fields are shortened until the report fits - the comments
        // first, because the description is what names the error.
        for (const Long of ["Comments", "ErrorDescription"]) {
            while (Self["kReportBytes"](Fit) > Self["kReportMaxBytes"] && String(Fit[Long]).length > 200) {
                Fit[Long] = String(Fit[Long]).substring(0, Math.floor(String(Fit[Long]).length * 0.7));
            }
        }
        return Fit;
    };

    /** DOCUMENTED EXCEPTION: pure check on the reporting path - reporting from it would recurse.
        True when a report must not leave the process: KERRORS_REPORT=off, or a node:test child
        (NODE_TEST_CONTEXT is set by `node --test`, verified on node 22.22) unless KERRORS_REPORT=on.
        Always false in a browser (no `process`). Test runs used to post real reports to kErrors. */
    public static KReportsAreSuppressed = (Self: typeof kCore): boolean => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const Proc: any = (globalThis as any).process;
        if (Proc === undefined || Proc === null || Proc.env === undefined || Proc.env === null) { return false; }
        const Setting: string = String(Proc.env.KERRORS_REPORT || "").toLowerCase();
        if (Setting === "on") { return false; }
        if (Setting === "off") { return true; }
        return Proc.env.NODE_TEST_CONTEXT !== undefined && Proc.env.NODE_TEST_CONTEXT !== "";
    };

    /** DOCUMENTED EXCEPTION: on the reporting path - reporting from it would recurse.
        When reports are suppressed, records the report in KReportsSuppressed, says so on stderr and
        answers true so KReportError stops before the POST. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static SuppressReport = (Self: typeof kCore, DataToSend: any): boolean => {
        if (Self.KReportsAreSuppressed() === false) { return false; }
        Self.KReportsSuppressed.push(DataToSend);
        let Description: string = "";
        if (DataToSend !== null && DataToSend !== undefined) { Description = String(DataToSend.ErrorDescription); }
        console.error("kCore.KReportError: not sent (test run or KERRORS_REPORT=off): " + Description);
        return true;
    };

    // ERROR-SUPPRESSED-JUSTIFIED: this IS the reporting path - the KErrorMode it would gate on is set by the
    // very error being reported, and reporting a failure of the report would recurse; the catch stays silent.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static KReportError = (Self: typeof kCore, DataToSend: any): void => {
        try {
            if (Self["kShouldReportError"](DataToSend) === false || Self["kSuppressReport"](DataToSend) === true) { return; }
            // The product version is a CRUCIAL part of every report and must never
            // be blank. Stamp it from CORE (kCore.KApplication, set at startup by
            // the browser `ks` AND the Azure Function) so both hosts inherit it.
            //
            // The Power Automate flow renders ProductName — NOT a separate version
            // field — so the version is carried INSIDE ProductName, matching the
            // browser's long-standing `name + " " + version` contract
            // (kalmstromShared4.KReportErrorMessage). A separate ProductVersion is
            // kept too, but it is the ProductName that reaches a human. The append
            // is idempotent (skipped when the name already contains the version) so
            // the browser, which builds ProductName WITH the version, is never
            // double-stamped; the Function, which sets a bare ProductName, gets it.
            if (DataToSend !== null && DataToSend !== undefined) {
                const Version: string = String(Self.KApplication.version);
                DataToSend.ProductVersion = Version;
                let ProductName: string = "";
                if (DataToSend.ProductName !== undefined
                    && DataToSend.ProductName !== null) {
                    ProductName = String(DataToSend.ProductName);
                }
                if (ProductName === "") {
                    ProductName = String(Self.KApplication.name);
                }
                if (Version !== "" && ProductName.indexOf(Version) === -1) {
                    ProductName = ProductName + " " + Version;
                }
                DataToSend.ProductName = ProductName;
            }
            fetch(Self.KServicesURL, {
                method: "POST",
                headers: { "Content-Type": "application/json; charset=utf-8" },
                body: JSON.stringify(Self.KFitReport(DataToSend)),
            }).then(Self.KReportErrorSettled, Self.KReportErrorSettled);
        } catch (e) {
            // Reporting must never throw out of the error handler.
        }
    };

    // DOCUMENTED EXCEPTION: on the reporting path (the 5-minute duplicate filter KReportError consults) - no guard, reporting from it would recurse.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static ShouldReportError = (Self: typeof kCore, data: any): boolean => {
        let Signature: string = "";
        if (data !== null && data !== undefined && data.ErrorDescription !== undefined) {
            Signature = String(data.ErrorDescription);
        }
        const Now: number = Date.now();
        const Same: boolean = Signature === Self["kLastErrorSignature"];
        const Recent: boolean = (Now - Self["kLastErrorReportedAt"]) < (5 * 60 * 1000);
        if (Same === true && Recent === true) { return false; }
        Self["kLastErrorSignature"] = Signature;
        Self["kLastErrorReportedAt"] = Now;
        return true;
    };
}
